import React, { useMemo } from 'react';
import * as Yup from 'yup';
import DepartmentCombobox from '../../../../shared/components/ui/DepartmentCombobox';
import number from '../../../../shared/util/utilities/number';
import { Form, Field } from '../../../../shared/components/Form';
import { FormSpy } from 'react-final-form';
import { IconButton, Modal, Group, Button, Spinner } from '@economic/gaudi';
import { t, useLocalization } from '../../../../shared/components/l10n/l10n';

function DistributionRow({ fields, key, values, index, name, readOnly }) {
    return (
        <div key={key} role="row" className="yt-table__row" style={{ height: '62px' }}>
            <div role="gridcell" className="yt-table__cell">
                {values.departmentNumber === 0 && <div className="text-left">{t`FORM_DISTRIBUTIONKEY_NODEPARTMENT`}</div>}
                {values.departmentNumber !== 0 && (
                    <Field.Combobox
                        readOnly={readOnly}
                        hideInlineSearch
                        omitDistributionKeys
                        className="mt-2"
                        name={`${name}.departmentNumber`}
                        as={DepartmentCombobox}
                    />
                )}
            </div>
            <div role="gridcell" className="yt-table__cell">
                <Field.Number readOnly={readOnly} defaultShowZero decimal className="mt-2" name={`${name}.percentage`} />
            </div>
            <div role="gridcell" className="yt-table__cell yt-table__cell__actions" style={{ flex: '0 0 45px' }}>
                <span className="yt-group">
                    {values.departmentNumber !== 0 && !readOnly && (
                        <IconButton
                            key={1}
                            appearance="discrete"
                            icon="delete"
                            aria-label="Delete"
                            onClick={() => fields.remove(index)}
                        />
                    )}
                </span>
            </div>
        </div>
    );
}

function DistributionTable({ fields, name, readOnly }) {
    return (
        <div role="table" className="yt-table">
            <div role="rowgroup" className="yt-table__head">
                <div role="row" className="yt-table__row">
                    <div role="columnheader" className="yt-table__cell yt-table__cell--disable-sort">
                        {t`FORM_DISTRIBUTIONKEY_DEPARTMENTNUMBER`}
                    </div>
                    <div role="columnheader" className="yt-table__cell yt-table__cell--disable-sort">
                        {t`FORM_DISTRIBUTIONKEY_DEPARTMENTDISTRIBUTIONPERCENT`}
                    </div>
                    <div
                        role="columnheader"
                        className="yt-table__cell yt-table__cell__actions yt-table__cell--disable-sort"
                        style={{ flex: '0 0 45px' }}>
                        &nbsp;
                    </div>
                </div>
            </div>
            <div role="rowgroup" className="yt-table__body">
                {fields.map((field, index) => (
                    <DistributionRow
                        fields={fields}
                        key={`row${index}`}
                        values={fields.value[index]}
                        index={index}
                        name={field}
                        readOnly={readOnly}
                    />
                ))}
                {!readOnly && (
                    <Button className="float-left m-3" appearance="default" onClick={() => fields.push({})}>
                        {t`FORM_DISTRIBUTIONKEY_ADDLINE`}
                    </Button>
                )}
            </div>
        </div>
    );
}

const validationSchema = Yup.object({
    distributionKeyNumber: Yup.number().required(),
    name: Yup.string().required(),
});

function DistributionKeyDialog({ disclosure, hide, distributionKey, headerText, onSubmit, allowChangeNumber = false }) {
    useMemo(() => {
        if (!distributionKey.hasBookedEntries) {
            //Append blank line for the no departments option if not existing
            if (!distributionKey.distributions.some(distribution => distribution.departmentNumber === 0)) {
                distributionKey.distributions.unshift({
                    percentage: 0,
                    departmentNumber: 0,
                });
            }
        }
    }, []);

    const loadingTexts = useLocalization(textIds);

    if (loadingTexts) {
        return (
            <Modal disclosure={disclosure} onHide={hide}>
                <Spinner />
            </Modal>
        );
    }

    function getPercentageDistributed(values) {
        return values.distributions
            .map(department => department.percentage)
            .filter(percentage => !isNaN(percentage))
            .reduce((accumulater, currentValue) => currentValue + accumulater);
    }

    async function handleSubmit(values) {
        await onSubmit(values);
    }

    const disableTable = distributionKey.hasBookedEntries;

    return (
        <Form onSubmit={handleSubmit} initialData={distributionKey} validationSchema={validationSchema}>
            <Modal
                aria-label={t`BOOKING_ERROR_DISTRIBUTIONKEY_DISTRIBUTIONKEY_HEADER`}
                disclosure={disclosure}
                onHide={hide}
                size="xs"
                extra={() => (
                    <FormSpy>
                        {({ values }) => {
                            const alreadyDistributed = getPercentageDistributed(values);
                            const remainingForDistribution = 100 - alreadyDistributed;

                            return (
                                <header className="flex -mt-4 mb-4 w-full px-4 rounded-b">
                                    <dl className="mb-0 py-2 w-1/3">
                                        <dt className="text-grey-darkest text-sm font-normal">{t`FORM_DISTRIBUTIONKEY_ALREADYDISTRIBUTED`}</dt>
                                        <dd className="mb-0 text-xl">{number.format(alreadyDistributed)}%</dd>
                                    </dl>
                                    <dl className="mb-0 py-2 w-1/3">
                                        <dt className="text-grey-darkest text-sm font-normal">{t`FORM_DISTRIBUTIONKEY_STILLTOBEDISTRIBUTED`}</dt>
                                        <dd className="mb-0 text-xl">{number.format(remainingForDistribution)}%</dd>
                                    </dl>
                                </header>
                            );
                        }}
                    </FormSpy>
                )}>
                <Modal.Header>{headerText}</Modal.Header>
                <Modal.Content>
                    <Group>
                        <Field.Number
                            label={t`FORM_DISTRIBUTIONKEY_NUMBER`}
                            className="mr-2 mt-2"
                            name="distributionKeyNumber"
                            readOnly={!allowChangeNumber}
                            style={{ width: '50px' }}
                        />
                        <Field.Text
                            maxLength="250"
                            label={t`FORM_DISTRIBUTIONKEY_NAME`}
                            className="mr-2 mt-2"
                            name="name"
                            style={{ width: '150px' }}
                        />
                        <Field.Checkbox text={t`FORM_DISTRIBUTIONKEY_BARRED`} className="mt-2" name="barred" />
                    </Group>
                    <Field.Array name="distributions">
                        {({ fields }) => {
                            return <DistributionTable fields={fields} name="distributions" readOnly={disableTable} />;
                        }}
                    </Field.Array>
                </Modal.Content>
                <Modal.Footer>
                    <Group>
                        <Button appearance="default" onClick={hide}>
                            {t`SHARED_FORM_CANCEL`}
                        </Button>
                        <FormSpy>
                            {({ form }) => (
                                <Button autoFocus appearance="primary" type="submit" onClick={form.submit}>
                                    {t`SHARED_FORM_OK`}
                                </Button>
                            )}
                        </FormSpy>
                    </Group>
                </Modal.Footer>
            </Modal>
        </Form>
    );
}

const textIds = [
    'BOOKING_ERROR_DISTRIBUTIONKEY_DISTRIBUTIONKEY_HEADER',
    'FORM_DISTRIBUTIONKEY_NUMBER',
    'FORM_DISTRIBUTIONKEY_NAME',
    'FORM_DISTRIBUTIONKEY_BARRED',
    'FORM_DISTRIBUTIONKEY_DEPARTMENTNUMBER',
    'FORM_DISTRIBUTIONKEY_DEPARTMENTDISTRIBUTIONPERCENT',
    'FORM_DISTRIBUTIONKEY_NODEPARTMENT',
    'FORM_DISTRIBUTIONKEY_ADDLINE',
    'SHARED_FORM_CANCEL',
    'SHARED_FORM_OK',
    'FORM_DISTRIBUTIONKEY_STILLTOBEDISTRIBUTED',
    'FORM_DISTRIBUTIONKEY_ALREADYDISTRIBUTED',
];

export default DistributionKeyDialog;
